(function () {
  if (typeof window === "undefined") return;
  if (window.__MEAMA_WV_OPTIMIZER_ACTIVE__) return;
  window.__MEAMA_WV_OPTIMIZER_ACTIVE__ = true;

  const managedStyleAttr = "data-wv-managed-style";
  const managedScriptAttr = "data-wv-managed-script";

  const cachePrefix = "meama_tab_cache_v3_";
  const cacheMetaKey = `${cachePrefix}__meta__`;
  const legacyCachePrefixes = ["meama_tab_cache_", "meama_tab_cache_v2_"];

  const deployRefreshVersion = "2026-05-14-r1";
  const deployRefreshKey = `${cachePrefix}deploy_refresh_${deployRefreshVersion}`;

  let cacheTtlMs = 10 * 60 * 1000;
  let maxCacheEntries = 8;
  let maxSnapshotBytes = 900 * 1024;

  const maintenanceCooldownMs = 15 * 1000;
  const prefetchCooldownMs = 3 * 1000;
  const backgroundFetchTimeoutMs = 9000;
  const navigationFetchTimeoutMs = 14000;
  const maxInMemorySnapshots = 4;

  const connection =
    navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const isSlowNetwork = !!(
    connection &&
    (connection.saveData || /(?:^|-)2g/i.test(String(connection.effectiveType || "")))
  );
  const deviceMemory = Number(navigator.deviceMemory || 0);
  const isLowMemoryDevice = Number.isFinite(deviceMemory) && deviceMemory > 0 && deviceMemory <= 2;

  if (isSlowNetwork || isLowMemoryDevice) {
    cacheTtlMs = 6 * 60 * 1000;
    maxCacheEntries = 5;
    maxSnapshotBytes = 650 * 1024;
  }

  const normalizePathname = (pathname) =>
    String(pathname || "").replace(/\/+$/, "") || "/";

  const stripLocalePrefix = (pathname) => {
    const normalized = normalizePathname(pathname);
    if (normalized === "/") return normalized;
    return normalized.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, "") || "/";
  };

  const toCanonicalPathname = (pathname) => stripLocalePrefix(pathname);

  const normalizeUrl = (input) => {
    try {
      let raw = input;
      if (typeof raw === "string" && /^shopify:\/\//i.test(raw)) {
        const relative = raw.replace(/^shopify:\/+/i, "");
        raw = `${location.origin}/${relative.replace(/^\/+/, "")}`;
      }

      const url = raw instanceof URL ? raw : new URL(raw, location.href);
      if (url.origin !== location.origin) return null;

      const pathname = normalizePathname(url.pathname);
      const canonicalPathname = toCanonicalPathname(pathname);
      const search = url.search || "";
      const originPath = `${url.origin}${pathname}`;
      const absolute = `${originPath}${search}`;

      return { url, pathname, canonicalPathname, search, originPath, absolute };
    } catch {
      return null;
    }
  };

  const getCurrentLocalePrefix = () => {
    const path = normalizePathname(location.pathname);
    if (path === "/") return "";
    const match = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
    return match ? `/${match[1]}` : "";
  };

  const isMeamaReactNativeWebView = (() => {
    const ua = navigator.userAgent || "";
    const hasBridge =
      !!window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.postMessage === "function";
    const hasAppUa = /MeamaApp/i.test(ua);

    if (hasBridge || hasAppUa) return true;

    const ios = /iPhone|iPod|iPad/i.test(ua);
    const safari = /Safari/i.test(ua);
    const standalone = window.navigator ? window.navigator.standalone : false;
    const androidWV = /\bwv\b|Android.*Version\/[\d.]+/i.test(ua);
    return (ios && (!safari || standalone === true)) || androidWV;
  })();

  if (!isMeamaReactNativeWebView) return;

  const runIdle = (fn, timeout = 400) =>
    "requestIdleCallback" in window
      ? requestIdleCallback(fn, { timeout })
      : setTimeout(fn, timeout);

  const shouldReduceNetworkWork = () =>
    document.visibilityState === "hidden" || isSlowNetwork;

  const blockedPathnames = new Set([]);
  const currentCanonicalPath = toCanonicalPathname(location.pathname);
  if (blockedPathnames.has(currentCanonicalPath)) return;

  // Refresh once per deployed script version (not every app launch).
  try {
    if (!localStorage.getItem(deployRefreshKey)) {
      localStorage.setItem(deployRefreshKey, String(Date.now()));
      location.replace(location.href);
      return;
    }
  } catch {}

  const hardReloadPathnames = new Set([
    "/",
    "/pages/locations",
    "/account/login",
    "/account/register",
    "/account",
    "/partial-pay",
    "/pages/partial-pay",
    "/cart",
  ]);
  const isHardReloadPath = (pathname) =>
    hardReloadPathnames.has(toCanonicalPathname(pathname));

  const isSensitiveFlowPath = (pathname) =>
    /^(?:\/checkout\b|\/account\b|\/cart\b|\/apps?\b|\/a\/|\/challenge\b)/i.test(
      toCanonicalPathname(pathname)
    );

  let firstPaintGuardApplied = false;
  const firstPaintGuardId = "wv-first-paint-hide";
  const applyFirstPaintGuard = () => {
    if (firstPaintGuardApplied) return;
    firstPaintGuardApplied = true;

    try {
      const style = document.createElement("style");
      style.id = firstPaintGuardId;
      style.setAttribute(managedStyleAttr, "1");
      style.textContent = `
        #shopify-section-announcement-bar, .announcement-bar { display: block !important; visibility: visible !important; opacity: 1 !important; }
        header, header.header, header.site-header, #shopify-section-header, [data-section-type="header"],
        footer, .footer, #shopify-section-footer, [data-section-type="footer"] { display: none !important; }
      `;
      const anchor = document.head.querySelector("style, link[rel='stylesheet']");
      if (anchor) document.head.insertBefore(style, anchor);
      else document.head.appendChild(style);
    } catch {}
  };

  const releaseFirstPaintGuard = () => {
    try {
      const s = document.getElementById(firstPaintGuardId);
      if (s) s.remove();
    } catch {}
  };

  try {
    if (!sessionStorage.getItem("wv_first_paint_hidden")) {
      sessionStorage.setItem("wv_first_paint_hidden", "1");
      applyFirstPaintGuard();
      window.addEventListener(
        "load",
        () => runIdle(releaseFirstPaintGuard, 600),
        { once: true }
      );
    }
  } catch {}

  let dockLinks = [];
  const allowedCacheTargets = new Set();
  const warmPrefetchSeen = new Map();

  const getLocalizedRoute = (canonicalPath) => {
    const prefix = getCurrentLocalePrefix();
    if (!canonicalPath || canonicalPath === "/") return prefix || "/";
    return `${prefix}${canonicalPath}`.replace(/\/{2,}/g, "/");
  };

  const addAllowedTarget = (pathOrUrl) => {
    const normalized = normalizeUrl(pathOrUrl);
    if (!normalized) return;
    if (isHardReloadPath(normalized.pathname)) return;
    allowedCacheTargets.add(normalized.originPath);
  };

  const refreshCacheTargets = () => {
    allowedCacheTargets.clear();

    const dockAnchors = [...document.querySelectorAll("mobile-dock a[href]")];
    const optInAnchors = [...document.querySelectorAll("a[href][data-wv-cache]")];
    const anchors = [...new Set([...dockAnchors, ...optInAnchors])];
    dockLinks = dockAnchors;

    anchors.forEach((a) => addAllowedTarget(a.getAttribute("href")));

    addAllowedTarget(getLocalizedRoute("/collections/all-products"));
    addAllowedTarget(getLocalizedRoute("/collections/offers"));
    addAllowedTarget(getLocalizedRoute("/"));
  };

  const getCacheKey = (abs) => cachePrefix + abs;

  let cacheMetaMemo = null;
  const inMemorySnapshots = new Map();

  const readCacheMeta = () => {
    if (cacheMetaMemo && typeof cacheMetaMemo === "object") return cacheMetaMemo;
    try {
      const raw = localStorage.getItem(cacheMetaKey);
      const meta = raw ? JSON.parse(raw) : {};
      cacheMetaMemo = meta && typeof meta === "object" ? meta : {};
      return cacheMetaMemo;
    } catch {
      cacheMetaMemo = {};
      return cacheMetaMemo;
    }
  };

  const writeCacheMeta = (meta) => {
    cacheMetaMemo = meta || {};
    try {
      localStorage.setItem(cacheMetaKey, JSON.stringify(cacheMetaMemo));
    } catch {}
  };

  const trimMemorySnapshots = () => {
    while (inMemorySnapshots.size > maxInMemorySnapshots) {
      const firstKey = inMemorySnapshots.keys().next().value;
      if (!firstKey) break;
      inMemorySnapshots.delete(firstKey);
    }
  };

  const rememberSnapshot = (key, snapshot) => {
    if (!snapshot) return;
    if (inMemorySnapshots.has(key)) inMemorySnapshots.delete(key);
    inMemorySnapshots.set(key, snapshot);
    trimMemorySnapshots();
  };

  let lastMaintenanceRunAt = 0;
  let maintenanceScheduled = false;

  const pruneCacheStorage = ({ aggressive = false } = {}) => {
    try {
      const meta = readCacheMeta();
      const now = Date.now();
      const entries = [];
      const toRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(cachePrefix) || key === cacheMetaKey) continue;

        const record = meta[key];
        const ts = record && Number.isFinite(record.ts) ? record.ts : 0;
        const age = ts ? now - ts : Number.POSITIVE_INFINITY;

        if (aggressive || age > cacheTtlMs) {
          toRemove.push(key);
          delete meta[key];
          continue;
        }

        entries.push({ key, ts });
      }

      entries.sort((a, b) => b.ts - a.ts);
      entries.slice(maxCacheEntries).forEach((entry) => {
        toRemove.push(entry.key);
        delete meta[entry.key];
      });

      toRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {}
        inMemorySnapshots.delete(key);
      });

      writeCacheMeta(meta);
      lastMaintenanceRunAt = Date.now();
    } catch {}
  };

  const scheduleMaintenance = (opts = {}) => {
    const now = Date.now();
    if (!opts.aggressive && now - lastMaintenanceRunAt < maintenanceCooldownMs) return;
    if (maintenanceScheduled) return;
    maintenanceScheduled = true;
    runIdle(() => {
      maintenanceScheduled = false;
      pruneCacheStorage(opts);
    }, 1000);
  };

  const touchCacheMeta = (key, bytes) => {
    try {
      const meta = readCacheMeta();
      meta[key] = { ts: Date.now(), bytes: bytes || 0 };
      writeCacheMeta(meta);
    } catch {}
  };

  const removeCacheEntry = (key) => {
    inMemorySnapshots.delete(key);
    try {
      localStorage.removeItem(key);
    } catch {}
    try {
      const meta = readCacheMeta();
      if (meta[key]) {
        delete meta[key];
        writeCacheMeta(meta);
      }
    } catch {}
  };

  const readCachedSnapshot = (key) => {
    try {
      if (inMemorySnapshots.has(key)) {
        const cached = inMemorySnapshots.get(key);
        touchCacheMeta(key, cached.length);
        return cached;
      }

      const raw = localStorage.getItem(key);
      if (!raw) return null;

      const meta = readCacheMeta()[key];
      if (meta && Number.isFinite(meta.ts) && Date.now() - meta.ts > cacheTtlMs) {
        removeCacheEntry(key);
        return null;
      }

      touchCacheMeta(key, raw.length);
      rememberSnapshot(key, raw);
      return raw;
    } catch {
      return null;
    }
  };

  const writeCachedSnapshot = (key, snapshot) => {
    if (!snapshot || snapshot.length > maxSnapshotBytes) return false;

    try {
      localStorage.setItem(key, snapshot);
      rememberSnapshot(key, snapshot);
      touchCacheMeta(key, snapshot.length);
      scheduleMaintenance();
      return true;
    } catch {
      try {
        pruneCacheStorage({ aggressive: true });
        localStorage.setItem(key, snapshot);
        rememberSnapshot(key, snapshot);
        touchCacheMeta(key, snapshot.length);
        scheduleMaintenance();
        return true;
      } catch {
        return false;
      }
    }
  };

  const createCacheSnapshot = (html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const main = doc.querySelector("main");
      if (!main) return null;

      const fragments = [];
      const title = doc.querySelector("title");
      if (title) fragments.push(title.outerHTML);

      doc.querySelectorAll("link[rel='stylesheet'],style").forEach((el) => {
        fragments.push(el.outerHTML);
      });

      doc.querySelectorAll("head script[data-wv-exec]").forEach((el) => {
        fragments.push(el.outerHTML);
      });

      return `<html><head>${fragments.join("")}</head><body>${main.outerHTML}</body></html>`;
    } catch {
      return null;
    }
  };

  const purgeLegacyCache = () => {
    try {
      legacyCachePrefixes.forEach((prefix) => {
        if (!prefix || prefix === cachePrefix) return;
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (!key || !key.startsWith(prefix)) continue;
          try {
            localStorage.removeItem(key);
          } catch {}
        }
      });

      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(cachePrefix) || key === cacheMetaKey) continue;
        const absolute = key.slice(cachePrefix.length);
        try {
          const u = new URL(absolute);
          const originPath = `${u.origin}${normalizePathname(u.pathname)}`;
          if (!allowedCacheTargets.has(originPath)) toRemove.push(key);
        } catch {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => removeCacheEntry(k));
    } catch {}
  };

  const waitForStylesheet = (link) =>
    new Promise((resolve) => {
      const done = () => resolve();
      link.addEventListener("load", done, { once: true });
      link.addEventListener("error", done, { once: true });
      setTimeout(done, 1200);
    });

  const buildStyleKey = (node) => {
    if (node.tagName === "LINK") return `link:${node.href}`;
    if (node.tagName === "STYLE") return `style:${(node.textContent || "").trim()}`;
    return null;
  };

  const buildScriptKey = (node) =>
    node.src ? `src:${node.src}` : `inline:${(node.textContent || "").trim()}`;

  const copyAllAttributes = (from, to) => {
    try {
      [...from.attributes].forEach((attr) => to.setAttribute(attr.name, attr.value));
    } catch {}
  };

  const syncPageStyles = async (doc) => {
    const nonManagedKeys = new Set();
    document.head
      .querySelectorAll(
        `link[rel='stylesheet']:not([${managedStyleAttr}]), style:not([${managedStyleAttr}])`
      )
      .forEach((n) => {
        const key = buildStyleKey(n);
        if (key) nonManagedKeys.add(key);
      });

    const managedExisting = new Map();
    document.head.querySelectorAll(`[${managedStyleAttr}]`).forEach((n) => {
      const key = buildStyleKey(n);
      if (!key) return;

      if (nonManagedKeys.has(key)) {
        n.remove();
        return;
      }

      if (!managedExisting.has(key)) managedExisting.set(key, n);
      else n.remove();
    });

    const presentKeys = new Set(nonManagedKeys);
    managedExisting.forEach((_, key) => presentKeys.add(key));

    const pending = [];
    const addStyle = (el) => {
      const key = buildStyleKey(el);
      if (!key) return;

      if (presentKeys.has(key)) {
        managedExisting.delete(key);
        return;
      }

      const clone = el.cloneNode(true);
      clone.setAttribute(managedStyleAttr, "1");
      if (clone.tagName === "LINK") clone.setAttribute("fetchpriority", "high");

      const anchor = document.head.querySelector("link[rel='stylesheet'], style");
      if (anchor) document.head.insertBefore(clone, anchor);
      else document.head.appendChild(clone);

      presentKeys.add(key);
      if (clone.tagName === "LINK") pending.push(waitForStylesheet(clone));
    };

    doc.querySelectorAll("link[rel='stylesheet'],style").forEach(addStyle);
    managedExisting.forEach((n) => n.remove());

    try {
      const nonManagedScriptKeys = new Set();
      document.head
        .querySelectorAll(`script[data-wv-exec]:not([${managedScriptAttr}])`)
        .forEach((s) => {
          const key = buildScriptKey(s);
          if (key) nonManagedScriptKeys.add(key);
        });

      const existingScripts = new Map();
      document.head.querySelectorAll(`script[${managedScriptAttr}]`).forEach((s) => {
        const key = buildScriptKey(s);
        if (!key) return;
        if (nonManagedScriptKeys.has(key)) {
          s.remove();
          return;
        }
        if (!existingScripts.has(key)) existingScripts.set(key, s);
        else s.remove();
      });

      const presentScriptKeys = new Set(nonManagedScriptKeys);
      existingScripts.forEach((_, key) => presentScriptKeys.add(key));

      doc.querySelectorAll("head script").forEach((old) => {
        if (!old.hasAttribute("data-wv-exec")) return;
        const key = buildScriptKey(old);
        if (!key || presentScriptKeys.has(key)) {
          existingScripts.delete(key);
          return;
        }

        const s = document.createElement("script");
        copyAllAttributes(old, s);
        if (!old.defer) s.defer = true;
        s.setAttribute(managedScriptAttr, "1");
        if (!old.src) s.textContent = old.textContent || "";

        const anchorScript = document.head.querySelector("script");
        if (anchorScript) document.head.insertBefore(s, anchorScript);
        else document.head.appendChild(s);

        presentScriptKeys.add(key);
      });
    } catch {}

    if (pending.length) await Promise.allSettled(pending);
  };

  let mainEl = document.querySelector("main") || document.body;
  let activeNav = null;
  let navFetchController = null;
  const backgroundControllers = new Set();
  const inFlightCacheRequests = new Map();
  let prefetchQueue = Promise.resolve();

  const isExecutableScriptType = (type) => {
    const t = String(type || "").trim().toLowerCase();
    return (
      !t ||
      t === "text/javascript" ||
      t === "application/javascript" ||
      t === "text/ecmascript" ||
      t === "application/ecmascript" ||
      t === "module"
    );
  };

  const executeInlineScripts = (root) => {
    try {
      root.querySelectorAll("script").forEach((old) => {
        if (!isExecutableScriptType(old.type)) return;
        if (old.src && !old.hasAttribute("data-wv-exec")) return;

        const s = document.createElement("script");
        copyAllAttributes(old, s);
        if (old.src && old.hasAttribute("data-wv-exec")) s.src = old.src;
        if (!old.src) s.textContent = old.textContent || "";
        old.replaceWith(s);
      });
    } catch {}
  };

  const notifyPageRehydrated = () => {
    activeNav = null;
    releaseFirstPaintGuard();

    window.dispatchEvent(new Event("meama:pageRehydrated"));

    try {
      document.dispatchEvent(new CustomEvent("page:loaded"));

      const sections = mainEl.querySelectorAll(
        'section[data-section-id], [data-section-id].shopify-section, [data-section-type]'
      );
      sections.forEach((el) => {
        el.dispatchEvent(new Event("shopify:section:load", { bubbles: true }));
        el.dispatchEvent(new Event("section:load", { bubbles: true }));
      });

      window.dispatchEvent(new Event("resize"));
      window.dispatchEvent(new Event("scroll"));
    } catch {}
  };

  const swapContent = async (html, newUrl, push = true) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const sourceMain = doc.querySelector("main") || doc.body;
    if (!sourceMain) return;

    await syncPageStyles(doc);

    const nextMain = sourceMain.cloneNode(true);
    document.title = doc.title || document.title;
    mainEl.replaceWith(nextMain);
    mainEl = nextMain;

    executeInlineScripts(mainEl);

    refreshCacheTargets();
    purgeLegacyCache();
    scheduleMaintenance();

    if (push) history.pushState({}, "", newUrl);

    if (push) {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch {
        try {
          window.scrollTo(0, 0);
        } catch {}
      }
    }

    notifyPageRehydrated();
  };

  const fetchWithTimeout = (url, options, timeoutMs, controller) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        try {
          controller.abort();
        } catch {}
      }, timeoutMs);

      fetch(url, { ...options, signal: controller.signal })
        .then(resolve)
        .catch(reject)
        .finally(() => clearTimeout(timer));
    });

  const abortBackgroundFetches = () => {
    backgroundControllers.forEach((c) => {
      try {
        c.abort();
      } catch {}
    });
    backgroundControllers.clear();
  };

  const updateCache = async (input, { priority = "background" } = {}) => {
    const descriptor = normalizeUrl(input);
    if (!descriptor) return null;
    if (isHardReloadPath(descriptor.pathname)) return null;
    if (!allowedCacheTargets.has(descriptor.originPath)) return null;
    if (priority !== "navigation" && shouldReduceNetworkWork()) return null;

    const key = getCacheKey(descriptor.absolute);
    if (inFlightCacheRequests.has(key)) return inFlightCacheRequests.get(key);

    const controller = new AbortController();
    if (priority !== "navigation") backgroundControllers.add(controller);

    const request = fetchWithTimeout(
      descriptor.url.href,
      { cache: "no-cache", credentials: "same-origin" },
      priority === "navigation" ? navigationFetchTimeoutMs : backgroundFetchTimeoutMs,
      controller
    )
      .then(async (res) => {
        if (!res.ok) return null;
        if (!res.headers.get("content-type")?.includes("text/html")) return null;

        const html = await res.text();
        const snapshot = createCacheSnapshot(html) || html;
        const finalDesc = normalizeUrl(res.url || descriptor.url.href) || descriptor;
        const finalKey = getCacheKey(finalDesc.absolute);

        writeCachedSnapshot(finalKey, snapshot);
        if (finalKey !== key) writeCachedSnapshot(key, snapshot);

        return snapshot;
      })
      .catch(() => null)
      .finally(() => {
        inFlightCacheRequests.delete(key);
        backgroundControllers.delete(controller);
      });

    inFlightCacheRequests.set(key, request);
    return request;
  };

  const enqueuePrefetch = (absoluteUrl) => {
    prefetchQueue = prefetchQueue
      .catch(() => null)
      .then(() => updateCache(absoluteUrl, { priority: "background" }));
    return prefetchQueue;
  };

  const sequentialPrecache = async (links) => {
    for (const a of links) {
      if (activeNav) return;
      const href = a.getAttribute("href");
      const n = normalizeUrl(href);
      if (!n || isHardReloadPath(n.pathname) || !allowedCacheTargets.has(n.originPath)) continue;
      if (isSensitiveFlowPath(n.pathname)) continue;

      await new Promise((resolve) =>
        runIdle(async () => {
          if (activeNav || shouldReduceNetworkWork()) return resolve();
          await enqueuePrefetch(n.absolute);
          resolve();
        }, 500)
      );
    }
  };

  const maybeWarmPrefetch = (href) => {
    const d = normalizeUrl(href);
    if (!d) return;
    if (isSensitiveFlowPath(d.pathname) || isHardReloadPath(d.pathname)) return;
    if (!allowedCacheTargets.has(d.originPath)) return;
    if (shouldReduceNetworkWork()) return;

    const lastSeen = warmPrefetchSeen.get(d.originPath) || 0;
    const now = Date.now();
    if (now - lastSeen < prefetchCooldownMs) return;
    warmPrefetchSeen.set(d.originPath, now);

    runIdle(() => enqueuePrefetch(d.absolute), 80);
  };

  const fetchAndSwap = (descriptor, { push } = { push: true }) => {
    const controller = new AbortController();
    navFetchController = controller;

    return fetchWithTimeout(
      descriptor.absolute,
      { credentials: "same-origin" },
      navigationFetchTimeoutMs,
      controller
    )
      .then(async (r) => {
        if (!r.ok) throw new Error("Network");
        const html = await r.text();
        return { html, final: normalizeUrl(r.url) || descriptor };
      })
      .then(({ html, final }) => {
        if (controller !== navFetchController) return;
        if (push && activeNav !== descriptor.absolute) return;

        const snapshot = createCacheSnapshot(html) || html;
        const finalKey = getCacheKey(final.absolute);
        writeCachedSnapshot(finalKey, snapshot);
        writeCachedSnapshot(getCacheKey(descriptor.absolute), snapshot);

        return swapContent(snapshot, final.url.href, push);
      })
      .catch(() => {
        location.href = descriptor.url.href;
      })
      .finally(() => {
        if (controller === navFetchController) navFetchController = null;
      });
  };

  refreshCacheTargets();
  purgeLegacyCache();
  pruneCacheStorage();

  window.addEventListener("load", () => {
    runIdle(async () => {
      refreshCacheTargets();
      purgeLegacyCache();
      scheduleMaintenance();

      if (!shouldReduceNetworkWork()) {
        await updateCache(getLocalizedRoute("/collections/all-products"));
        await updateCache(getLocalizedRoute("/collections/offers"));
        sequentialPrecache(dockLinks);
      }
    }, 150);

    runIdle(() => {
      if (typeof window.initMeamaMap === "function") window.initMeamaMap();
      if (typeof window.initCarousels === "function") window.initCarousels();
    }, 600);
  });

  document.body.addEventListener(
    "touchstart",
    (e) => {
      const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!a) return;
      maybeWarmPrefetch(a.getAttribute("href"));
    },
    { passive: true }
  );

  document.body.addEventListener(
    "pointerdown",
    (e) => {
      if (typeof e.button === "number" && e.button !== 0) return;
      const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!a) return;
      maybeWarmPrefetch(a.getAttribute("href"));
    },
    { passive: true }
  );

  document.body.addEventListener("click", (e) => {
    if (e.defaultPrevented) return;
    if (typeof e.button === "number" && e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (!a) return;

    const href = a.getAttribute("href");
    if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;
    if (a.hasAttribute("download")) return;
    if (a.hasAttribute("data-no-instant") || a.hasAttribute("data-no-wv")) return;

    const target = a.getAttribute("target");
    if (target && target.toLowerCase() !== "_self") return;

    const descriptor = normalizeUrl(href);
    if (!descriptor) return;

    const samePath =
      normalizePathname(descriptor.pathname) === normalizePathname(location.pathname);
    const sameQuery = (descriptor.search || "") === (location.search || "");
    if (samePath && sameQuery) return;

    if (isSensitiveFlowPath(descriptor.pathname)) return;
    if (isHardReloadPath(descriptor.pathname) || !allowedCacheTargets.has(descriptor.originPath))
      return;

    e.preventDefault();
    activeNav = descriptor.absolute;

    abortBackgroundFetches();
    if (navFetchController) navFetchController.abort();

    const key = getCacheKey(descriptor.absolute);
    const cached = readCachedSnapshot(key);

    if (cached) {
      swapContent(cached, descriptor.url.href).then(() =>
        runIdle(() => updateCache(descriptor.absolute), 200)
      );
      return;
    }

    fetchAndSwap(descriptor, { push: true });
  });

  window.addEventListener("popstate", () => {
    const d = normalizeUrl(location.href);
    if (!d) return;
    if (isHardReloadPath(d.pathname) || !allowedCacheTargets.has(d.originPath)) return;

    let cached = readCachedSnapshot(getCacheKey(d.absolute));
    if (!cached) cached = readCachedSnapshot(getCacheKey(d.originPath));

    if (cached) {
      swapContent(cached, d.url.href, false);
      runIdle(() => updateCache(d.absolute), 50);
      return;
    }

    fetchAndSwap(d, { push: false });
  });

  window.addEventListener("pageshow", (e) => {
    if (e.persisted) {
      releaseFirstPaintGuard();
      refreshCacheTargets();
      purgeLegacyCache();
      scheduleMaintenance();
    }
  });

  window.addEventListener("meama:pageRehydrated", () => {
    runIdle(() => {
      if (typeof window.initMeamaMap === "function") window.initMeamaMap();
      if (typeof window.initCarousels === "function") window.initCarousels();
    }, 200);

    if (!shouldReduceNetworkWork()) {
      runIdle(() => sequentialPrecache(dockLinks), 600);
    }
  });

  window.addEventListener("pagehide", () => {
    try {
      const d = normalizeUrl(location.href);
      if (!d || !allowedCacheTargets.has(d.originPath)) return;
      const snapshot =
        createCacheSnapshot(document.documentElement.outerHTML) ||
        document.documentElement.outerHTML;
      writeCachedSnapshot(getCacheKey(d.absolute), snapshot);
    } catch {}
  });
})();
